import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function extractListPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const keysToCheck = [...preferredKeys, 'data', 'items', 'results', 'vendors', 'users'];
  for (const key of keysToCheck) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (isPlainObject(value)) {
      const nested = extractListPayload(value, preferredKeys);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function extractEntityPayload(payload, preferredKeys = []) {
  if (!isPlainObject(payload)) return null;

  const keysToCheck = [...preferredKeys, 'data', 'item', 'result', 'vendor', 'user'];
  for (const key of keysToCheck) {
    if (isPlainObject(payload[key])) return payload[key];
  }

  return payload;
}

function getVendorId(vendor) {
  return vendor?.id ?? vendor?.user_id ?? vendor?.userId ?? vendor?.vendor_id ?? null;
}

export const fetchVendors = createAsyncThunk(
  'vendor/fetchVendors',
  async (filters, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/vendors', { params: filters });
      return extractListPayload(data, ['vendors']);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch vendors');
    }
  }
);

export const fetchPendingVendors = createAsyncThunk(
  'vendor/fetchPendingVendors',
    async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/admin/pending-vendors');
      return extractListPayload(data, ['pendingVendors', 'pending_vendors', 'vendors', 'users']);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch pending vendors');
    }
  }
);

export const approveVendor = createAsyncThunk(
  'vendor/approveVendor',
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/admin/approve-vendor/${userId}`);
      return {
        userId,
        vendor: extractEntityPayload(data, ['vendor', 'user']),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to approve vendor');
    }
  }
);

export const fetchVendorById = createAsyncThunk(
  'vendor/fetchVendorById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/vendors/${id}`);
      return extractEntityPayload(data, ['vendor', 'user']) ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch vendor');
    }
  }
);

export const createVendor = createAsyncThunk(
  'vendor/createVendor',
  async (vendorData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/vendors', vendorData);
      return data?.data ?? data ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create vendor');
    }
  }
);

export const updateVendor = createAsyncThunk(
  'vendor/updateVendor',
  async ({ id, vendorData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/vendors/${id}`, vendorData);
      return data?.data ?? data ?? {} ;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update vendor');
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'vendor/deleteVendor',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/vendors/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete vendor');
    }
  }
);

const initialState = {
  vendors: [],
  pendingVendors: [],
  selectedVendor: null,
  loading: false,
  error: null,
};

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    selectVendor: (state, action) => {
      state.selectedVendor = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch vendors';
      })
      .addCase(fetchPendingVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingVendors = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPendingVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch pending vendors';
      })
      .addCase(approveVendor.fulfilled, (state, action) => {
        const approvedId = getVendorId(action.payload?.vendor) ?? action.payload?.userId;
        state.pendingVendors = state.pendingVendors.filter((vendor) => getVendorId(vendor) !== approvedId);
      })
      .addCase(approveVendor.rejected, (state, action) => {
        state.error = action.payload || 'Failed to approve vendor';
      })
      .addCase(fetchVendorById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedVendor = action.payload;
      })
      .addCase(fetchVendorById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch vendor';
      })
      .addCase(createVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors.push(action.payload);
      })
      .addCase(createVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create vendor';
      })
      .addCase(updateVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVendor.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.vendors.findIndex((v) => v.id === action.payload.id);
        if (index !== -1) state.vendors[index] = action.payload;
        if (state.selectedVendor?.id === action.payload.id) {
          state.selectedVendor = action.payload;
        }
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update vendor';
      })
      .addCase(deleteVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = state.vendors.filter((v) => v.id !== action.payload);
        if (state.selectedVendor?.id === action.payload) {
          state.selectedVendor = null;
        }
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete vendor';
      });
  },
});

export const { selectVendor, clearError } = vendorSlice.actions;
export default vendorSlice.reducer;

export const selectVendors = (state) => state.vendor.vendors;
export const selectSelectedVendor = (state) => state.vendor.selectedVendor;
export const selectVendorLoading = (state) => state.vendor.loading;
export const selectVendorError = (state) => state.vendor.error;
